/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/legacy/x-data-view.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { AttendanceExportEventlog } from '@chancenwerk/shared/eventlogs/AttendanceExportEventlog.js';
import { KpfFilter } from '@chancenwerk/shared/filter/KpfFilter.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-checkbox.js';
import '@chancenwerk/client/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import '@chancenwerk/client/design/components/cr-loader.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { ProductAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/ProductAspect.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { of, switchMap } from 'rxjs';
import type { KPF } from '../../../../src/master-data-management/kpf/entities/KPF.js';
import '../components/board-component-course-digital-card.js';
import '../components/board-component-kpf-filter.js';
import { globalKpfFilter } from '../globalSubjects.js';
import { BoardRoutes } from './../routes.js';

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _id: 1,
  _class: 1,
  kuerzel: 1,
  attendanceLogsCount: 1,
  school: { _id: 1, _class: 1, kuerzel: 1, name: 1 },
  produkt: { _id: 1, _class: 1, kuerzel: 1 },
};

/**
 * @customElement board-page-attendance-export-history
 */
@customElement('board-page-attendance-export-history')
export class BoardPageAttendanceExportHistory extends CrPage {
  override styles() {
    return [...super.styles()];
  }

  constructor() {
    super();
    this.label = 'Anwesenheitsexport Historie';
    this.description = 'Hier kannst du dir die Historie der vergangenen Exporte anschauen.';
    this.permission = ChancenPermission.board.EXPORT_ATTENDANCE_HISTORY;
  }

  @property({ attribute: false }) private readonly _globalFilter = new RXController(this, globalKpfFilter);

  private readonly _kpfsPaginated = new SubscriptionPagination<KPF, Array<KpfFilter | undefined>>(
    (skip, limit, filter: KpfFilter, gqlFields: BaseQLSelectionSet<KPF>) => (gqlFields: BaseQLSelectionSet<KPF>) =>
      KPFAPI.subscribeHistoryByFilter(skip, limit, filter)(gqlFields),
    [
      new KpfFilter({
        aspects: [
          new BooleanAspect({ customID: 'active', value: true }),
          new ProductAspect({ value: ProductLKK._class }),
        ],
      }),
    ],
    kpfSelector,
    10000, // todo: too many entries
  );

  @state() private readonly _kpfs = new RXController(this, this._kpfsPaginated.data);

  @state() private readonly _selectedKpf = new RXController<KPF | null>(this, null);

  @state() private readonly _exportLogs = new RXController(
    this,
    this._selectedKpf.observable,
    observable => {
      return observable.pipe(
        switchMap(selectedKpf => {
          if (!selectedKpf) return of([]);

          return AttendanceExportEventlog.getEntitiesByKpf(selectedKpf._id)(
            AttendanceExportEventlog.attendanceExportDefaultSet,
          );
        }),
      );
    },
    { pipeIsLoading: true },
  );

  override connectedCallback() {
    super.connectedCallback();

    this._kpfsPaginated.updateParameters(
      new KpfFilter({
        aspects: [
          new BooleanAspect({ customID: 'active', value: true }),
          new ProductAspect({ value: ProductLKK._class }),
        ],
      }),
    );
  }

  override content() {
    return html`
      <cr-flex space="sm" padding="xs">
        <board-component-kpf-filter .kpfsPaginated=${this._kpfsPaginated}>
          <chancen-component-card-title slot="front">Export Historie</chancen-component-card-title>
          <cr-flex space="md" horizontal>
            <cr-dropdown-selection
              slot="right"
              clearable
              .value=${this._selectedKpf.value}
              flex
              icon="search"
              placeholder="KPF"
              .selectables=${this._kpfs.value}
              .renderString=${(entry: Maybe<KPF>) => {
                if (!entry) return 'KPF auswählen';
                return `${entry.stringRepresentation}${
                  entry.attendanceLogsCount ? ` | ${entry.attendanceLogsCount} Einträge` : ''
                }`;
              }}
              @value-picked=${(e: CustomEvent<{ value: KPF }>) => {
                this._selectedKpf.next(e.detail.value);
              }}
            ></cr-dropdown-selection>
          </cr-flex>
        </board-component-kpf-filter>
        ${this._getView()}
      </cr-flex>
    `;
  }

  private _getView() {
    const kpfsLoading = this._kpfsPaginated.loadingInitially.value || this._kpfs.isLoading;

    if (kpfsLoading) {
      return html`<cr-flex crossaxis-center><cr-loader></cr-loader></cr-flex>`;
    } else if (this._kpfs.value.length) {
      return this._kpfView();
    } else {
      return this._placeholderView();
    }
  }

  private _placeholderView() {
    return html`<cr-flex space="md" vertical>
      <cr-text>Es wurden bisher keine Inhalte exportiert, oder kein KPF entspricht deinen Filterkriterien.</cr-text>
      <cr-flex space="md" horizontal>
        <cr-button href=${BoardRoutes.attendanceExport}>Zum Export</cr-button>
        <cr-button @click=${this._resetGlobalKpfFilter} mode="outline">Filter Löschen</cr-button>
      </cr-flex>
    </cr-flex>`;
  }

  private _kpfView() {
    return html`
      <cr-flex space="md">
        <div>
          ${this._exportLogs.value?.length
            ? html`${this._logList()}`
            : this._selectedKpf.value
              ? html`<c-flex padding="md"><cr-text>Für diese KPF liegen keine Exportlogs vor.</cr-text></c-flex>`
              : ''}
        </div>
      </cr-flex>
    `;
  }

  private _logList() {
    return html`
      <cr-card space="md" ${css({ overflowX: 'scroll' })}>
        <chancen-component-card-title>Historie</chancen-component-card-title>
        <cr-data-view-table
          .fullOptions=${buildFullOptions({
            class: AttendanceExportEventlog,
            loader: () => nothing,
            onlyShowLastTranslation: true,
            customColumns: this._colDefs,
            selectionSet: AttendanceExportEventlog.attendanceExportDefaultSet,
          } satisfies DataViewOptions<typeof AttendanceExportEventlog>).dataViewOptions}
          .data=${this._exportLogs.value}
        >
        </cr-data-view-table>
      </cr-card>
    `;
  }

  private readonly _resetGlobalKpfFilter = () => {
    const attributes = ['productClass', 'search', 'searchFields', 'state', 'zipCity'];

    attributes.forEach(attr => (this._globalFilter.value[attr] = null));
    this._globalFilter.next(this._globalFilter.value);
  };

  private readonly _colDefs: ColumnDefinition<typeof AttendanceExportEventlog>[] = [
    {
      columnName: 'KPF',
      columnWidth: '1fr',
      customID: 'KPF',
      getFieldMapping(entry) {
        return entry.kpf?.stringRepresentation;
      },
    },
    {
      columnName: 'Kurse',
      fieldName: 'courses',
      columnWidth: '3fr',
      getFieldMapping(entry) {
        return entry.courses?.map(c => {
          return html`<cr-text>${c.stringRepresentation}</cr-text>`;
        });
      },
    },
    { columnName: 'Typ', fieldName: 'type', columnWidth: '1fr' },
    {
      columnName: 'Exportiert durch',
      fieldName: 'user',
      columnWidth: '1fr',
      selectionSet: ChancenwerkUser.toStringSelectionSet,
    },
    { columnName: 'Exportierter Bereich', fieldName: 'action', columnWidth: '1fr' },
    {
      columnName: 'Exportierter Zeitraum',
      customID: 'exportierter_zeitraum',
      columnWidth: '1fr',
      getFieldMapping(entry) {
        return entry.startDate.toFormat('dd.MM.yyyy') + ' bis ' + entry.endDate.toFormat('dd.MM.yyyy');
      },
    },
    {
      columnName: 'Exportiert am',
      customID: 'exportiert_am',
      columnWidth: '1fr',
      getFieldMapping(entry) {
        return DateTime.fromJSDate(entry.createdAt).toFormat('dd.MM.yyyy HH:mm:ss');
      },
    },
  ];
}
